import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { label } from "./cityMeta";

const LIFESTYLE = ["food", "nightlife", "outdoors", "beach", "green", "arts", "museums", "live_music", "cafe", "festivals", "sports_participate", "sports_watch", "diverse"];
const tone = (s) => (s >= 75 ? "#047857" : s >= 50 ? "#B45309" : "#B91C1C");

function LifestylePicker({ prefs, onSaved }) {
  const [sel, setSel] = useState(prefs);
  const [busy, setBusy] = useState(false);
  const toggle = (t) => setSel((s) => (s.includes(t) ? s.filter((x) => x !== t) : [...s, t]));
  const save = async () => {
    setBusy(true);
    try {
      await api.post("/profile", { lifestyle_prefs: sel });
      toast.success("Saved what you enjoy in a city");
      onSaved();
    } catch { toast.error("Could not save"); } finally { setBusy(false); }
  };
  return (
    <div className="mt-3 border-t border-stone-200 pt-3" data-testid="featured-cities-lifestyle-picker">
      <p className="text-xs text-stone-600 mb-2">What do you enjoy in a city?</p>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {LIFESTYLE.map((t) => (
          <button key={t} type="button" onClick={() => toggle(t)} aria-pressed={sel.includes(t)}
            className={`text-xs px-2 py-1 rounded-full border transition-colors ${sel.includes(t) ? "bg-stone-900 text-white border-stone-900" : "border-stone-300 text-stone-700 hover:bg-stone-100"}`}
            data-testid={`featured-cities-pref-${t}`}>{label(t)}</button>
        ))}
      </div>
      <button type="button" onClick={save} disabled={busy} className="text-xs font-medium px-3 py-1.5 rounded-full bg-[#4C1D95] text-white disabled:opacity-50" data-testid="featured-cities-pref-save">Save</button>
    </div>
  );
}

export function MatchBadge({ match, prefs, cityName, onRefresh, title, withLifestyle = true, tid = "featured-cities" }) {
  const { user, openSignIn } = useAuth();
  if (!user) {
    return (
      <button type="button" onClick={openSignIn} className="inline-flex items-center gap-1.5 rounded-full bg-white/90 text-stone-900 text-xs font-medium px-3 py-1.5 hover:bg-white transition-colors" data-testid={`${tid}-match-signin`}>
        <Sparkles className="h-3.5 w-3.5" /> Sign in to see your match
      </button>
    );
  }
  if (!match) return null;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className="inline-flex items-center gap-2 rounded-full bg-white text-stone-900 px-3 py-1.5 shadow-sm hover:-translate-y-0.5 transition-transform" data-testid={`${tid}-match-badge`}>
          <Sparkles className="h-4 w-4" style={{ color: match.score != null ? tone(match.score) : "#78716C" }} />
          {match.score != null
            ? <span className="font-data text-sm font-semibold" data-testid={`${tid}-match-score`}>{match.score}% match</span>
            : <span className="text-xs font-medium">See your match</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80" data-testid={`${tid}-match-popover`}>
        <p className="font-editorial text-lg text-stone-900 mb-2">{title || `How ${cityName} fits you`}</p>
        <ul className="space-y-2">
          {match.parts.map((p) => (
            <li key={p.key} data-testid={`${tid}-match-part-${p.key}`}>
              <p className="flex justify-between text-sm"><span className="font-medium">{p.label}</span><span className="font-data" style={{ color: tone(p.score) }}>{p.score}%</span></p>
              <p className="text-xs text-stone-600">{p.reason}</p>
            </li>
          ))}
        </ul>
        {match.nudges.length > 0 && (
          <div className="mt-3 text-xs text-stone-600" data-testid={`${tid}-match-nudges`}>
            To sharpen this: {match.nudges.filter((n) => !n.startsWith("Pick")).join(" · ")}
            {match.missing.some((m) => m !== "lifestyle") && <> · <Link to="/profile" className="underline">Edit profile</Link></>}
          </div>
        )}
        {withLifestyle && <LifestylePicker prefs={prefs} onSaved={onRefresh} />}
        <p className="font-data text-[10px] text-stone-400 mt-3">{withLifestyle ? "Uses your saved budget, subject, career direction and city likes, weighted by your fit sliders." : "Uses your saved subject, career direction and A-level grades."}</p>
      </PopoverContent>
    </Popover>
  );
}
